/**
 * Invoice Service - API functions for invoice metadata operations
 */

import { apiClient } from '@/lib/api'

export interface ClientSummary {
  id: string
  name: string
  status: string
  vat_code?: string
  mssql_client_id?: string
}

export interface CounterpartySummary {
  id: number
  name: string | null
  name_lat?: string | null
  code?: string | null
  vat_code?: string | null
  address?: string | null
  address_lat?: string | null
  city_name?: string | null
  city_name_lat?: string | null
  country?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  phone?: string | null
  email?: string | null
  flags?: {
    is_contragent?: boolean
    is_client?: boolean
    is_supplier?: boolean
    is_creditor?: boolean
    is_credit_taken?: boolean
    is_active?: boolean
  }
}

export interface LineItemErpMetadata {
  line_item_group: string
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
}

export interface InvoiceMetadata {
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  account_codes?: string[]
  vat_rate?: number
  business_reasoning?: string
  line_items?: LineItemErpMetadata[]
  [key: string]: any
}

export interface UpdateMetadataResponse {
  success: boolean
  message?: string
  rule_generation_job_id?: string | null
}

export interface LineItemsUpdateResponse {
  success: boolean
  message?: string
  extracted_data?: any[]
  representation?: any
  additional_metadata?: any
}

export interface InvoiceLineItemUpdatePayload {
  source_group_id?: string
  description: string
  quantity: string
  unit_price: string
  unit?: string
  line_total: string
  vat_rate?: string
  merged_from?: string[]
}

export async function fetchClients({ hasInvoices = false }: { hasInvoices?: boolean } = {}): Promise<ClientSummary[]> {
  try {
    const params = new URLSearchParams({ limit: '1000', status: 'active' })
    if (hasInvoices) params.append('has_invoices', 'true')
    const response = await apiClient.get<{ clients: ClientSummary[] }>(`/clients/?${params}`)
    return response.clients ?? []
  } catch (error) {
    console.error('Error loading clients:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to load clients')
  }
}

export async function updateRepresentationLineItem(
  invoiceId: string,
  lineItemGroup: string,
  field: string,
  value: string
): Promise<any> {
  return apiClient.patch(
    `/invoices/${invoiceId}/representation-line-items/${lineItemGroup}`,
    { field, value }
  )
}

export async function fetchInvoiceCounterparties(invoiceId: string): Promise<CounterpartySummary[]> {
  try {
    const response = await apiClient.get<{ counterparties: CounterpartySummary[] }>(
      `/invoices/${invoiceId}/counterparties`
    )
    return response.counterparties ?? []
  } catch (error) {
    console.error('Error loading invoice counterparties:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to load invoice counterparties'
    )
  }
}

/**
 * Update invoice metadata
 * @param invoiceId - The invoice ID
 * @param metadata - The metadata to update
 * @returns Promise with update response
 */
export async function updateInvoiceMetadata(
  invoiceId: string,
  metadata: Partial<InvoiceMetadata>
): Promise<UpdateMetadataResponse> {
  try {
    const response = await apiClient.put<UpdateMetadataResponse>(
      `/invoices/${invoiceId}/metadata`,
      { erp: metadata }
    )
    return response
  } catch (error) {
    console.error('Error updating invoice metadata:', error)
    throw new Error(
      error instanceof Error
        ? error.message
        : 'Failed to update invoice metadata'
    )
  }
}

export interface CandidateIdentifier {
  label: string
  identifier_type: 'vat' | 'name'
  identifier_value: string
  identifier_value_normalized: string
  existing_identification?: {
    id: string
    client_id: string
    client_name: string | null
    identifier_type: string
  }
}

export interface UpdateInvoiceClientResponse {
  message: string
  client_id: string | null
  candidate_identifiers?: CandidateIdentifier[]
}

export async function updateInvoiceClient(
  invoiceId: string,
  clientId: string | null
): Promise<UpdateInvoiceClientResponse> {
  try {
    return await apiClient.put<UpdateInvoiceClientResponse>(
      `/invoices/${invoiceId}/client`,
      { client_id: clientId }
    )
  } catch (error) {
    console.error('Error updating invoice client:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update invoice client'
    )
  }
}

export async function confirmClientIdentifications(
  clientId: string,
  identifications: Array<{
    identifier_type: string
    identifier_value: string
    existing_identification_id?: string | null
  }>,
  sourceInvoiceId?: string
): Promise<{ created: number; updated: number }> {
  return apiClient.post('/client-identifications/confirm', {
    client_id: clientId,
    identifications,
    source_invoice_id: sourceInvoiceId ?? null,
  })
}

export async function updateInvoiceLineItems(
  invoiceId: string,
  lineItems: InvoiceLineItemUpdatePayload[]
): Promise<LineItemsUpdateResponse> {
  try {
    const response = await apiClient.put<LineItemsUpdateResponse>(
      `/invoices/${invoiceId}/line-items`,
      { line_items: lineItems }
    )
    return response
  } catch (error) {
    console.error('Error updating invoice line items:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update invoice line items'
    )
  }
}

export async function updateInvoiceCounterparty(
  invoiceId: string,
  counterpartyId: number | null
): Promise<{
  message: string
  counterparty_id: number | null
  counterparty: CounterpartySummary | null
}> {
  try {
    return await apiClient.put<{
      message: string
      counterparty_id: number | null
      counterparty: CounterpartySummary | null
    }>(`/invoices/${invoiceId}/counterparty`, { counterparty_id: counterpartyId })
  } catch (error) {
    console.error('Error updating invoice counterparty:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update invoice counterparty'
    )
  }
}

// Configuration-based approach: Define field mappings
const FIELD_MAPPINGS = {
  dealName: 'deal_name',
  accountAssignment: 'acc_assignment',
  analyticParts: 'analytic_parts',
  businessReasoning: 'business_reasoning',
  vatRate: 'vat_rate',
  accountCodes: 'account_codes'
} as const

type FieldMappingKeys = keyof typeof FIELD_MAPPINGS

/**
 * Generic update function using field mappings
 */
function createFieldUpdater<K extends FieldMappingKeys>(
  fieldKey: K,
  _fieldName: string
) {
  return async function (
    invoiceId: string,
    value: NonNullable<InvoiceMetadata[typeof FIELD_MAPPINGS[K]]>
  ): Promise<UpdateMetadataResponse> {
    return updateInvoiceMetadata(invoiceId, {
      [FIELD_MAPPINGS[fieldKey]]: value
    } as Partial<InvoiceMetadata>)
  }
}

/**
 * Update deal name for an invoice
 * @param invoiceId - The invoice ID
 * @param dealNameId - The deal name ID to set
 * @returns Promise with update response
 */
export const updateDealName = createFieldUpdater('dealName', 'deal name')

/**
 * Update account assignment for an invoice
 * @param invoiceId - The invoice ID
 * @param accAssignmentId - The account assignment ID to set
 * @returns Promise with update response
 */
export const updateAccountAssignment = createFieldUpdater('accountAssignment', 'account assignment')

/**
 * Update analytic parts for an invoice
 * @param invoiceId - The invoice ID
 * @param analyticParts - The analytic parts to set
 * @returns Promise with update response
 */
export const updateAnalyticParts = createFieldUpdater('analyticParts', 'analytic parts')

/**
 * Update business reasoning for an invoice
 * @param invoiceId - The invoice ID
 * @param businessReasoning - The business reasoning text
 * @returns Promise with update response
 */
export const updateBusinessReasoning = createFieldUpdater('businessReasoning', 'business reasoning')

/**
 * Update VAT rate for an invoice
 * @param invoiceId - The invoice ID
 * @param vatRate - The VAT rate percentage
 * @returns Promise with update response
 */
export const updateVatRate = createFieldUpdater('vatRate', 'VAT rate')

/**
 * Update account codes for an invoice
 * @param invoiceId - The invoice ID
 * @param accountCodes - Array of account codes
 * @returns Promise with update response
 */
export const updateAccountCodes = createFieldUpdater('accountCodes', 'account codes')

/**
 * Batch update multiple metadata fields
 * @param invoiceId - The invoice ID
 * @param updates - Object containing multiple metadata updates
 * @returns Promise with update response
 */
export async function batchUpdateMetadata(
  invoiceId: string,
  updates: Partial<InvoiceMetadata>
): Promise<UpdateMetadataResponse> {
  return updateInvoiceMetadata(invoiceId, updates)
}

/**
 * Update ERP metadata for a specific line item
 * @param invoiceId - The invoice ID
 * @param lineItemGroup - The line item group ID
 * @param metadata - The ERP metadata for the line item (deal_name, acc_assignment, analytic_parts)
 * @returns Promise with update response
 */
export async function updateLineItemErpMetadata(
  invoiceId: string,
  lineItemGroup: string,
  metadata: Omit<LineItemErpMetadata, 'line_item_group'>
): Promise<UpdateMetadataResponse> {
  try {
    const response = await apiClient.put<UpdateMetadataResponse>(
      `/invoices/${invoiceId}/line-items/${encodeURIComponent(lineItemGroup)}/erp-metadata`,
      metadata
    )
    return response
  } catch (error) {
    console.error('Error updating line item ERP metadata:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update line item ERP metadata'
    )
  }
}

export async function restoreMergedLineItems(
  invoiceId: string,
  mergedGroupId: string
): Promise<LineItemsUpdateResponse> {
  try {
    const response = await apiClient.post<LineItemsUpdateResponse>(
      `/invoices/${invoiceId}/line-items/${encodeURIComponent(mergedGroupId)}/restore`
    )
    return response
  } catch (error) {
    console.error('Error restoring merged line items:', error)
    throw new Error(
      error instanceof Error ? error.message : 'Failed to restore merged line items'
    )
  }
}

function extractFilenameFromHeader(headerValue: string | null): string | null {
  if (!headerValue) return null

  const filenameMatch = headerValue.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
  if (filenameMatch) {
    const encodedName = filenameMatch[1] || filenameMatch[2]
    try {
      return decodeURIComponent(encodedName)
    } catch (_) {
      return encodedName
    }
  }

  return null
}

function ensureXmlExtension(filename: string, fallbackId: string): string {
  const baseName = filename ? filename.replace(/\.[^/.]+$/, '') : `invoice-${fallbackId}`
  return `${baseName}.xml`
}

export async function exportInvoice(invoiceId: string, fallbackFilename?: string): Promise<void> {
  const response = await apiClient.fetch(`/invoices/${invoiceId}/export`, {
    method: 'GET'
  })

  if (!response.ok) {
    let errorMessage = 'Failed to export invoice'
    try {
      const data = await response.json()
      errorMessage = data?.detail ?? errorMessage
    } catch (_) {
      // Ignore JSON parsing errors
    }
    throw new Error(errorMessage)
  }

  const blob = await response.blob()
  const headerFilename = extractFilenameFromHeader(response.headers.get('Content-Disposition'))
  const finalFilename = ensureXmlExtension(headerFilename ?? fallbackFilename ?? `invoice-${invoiceId}`, invoiceId)
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = finalFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

export interface ReprocessResult {
  message: string
  invoice_id: string
  job_id: string
  status: string
  children_deleted: number
}

export interface BulkReprocessResult {
  total: number
  succeeded: number
  failed: number
  results: Array<{ invoice_id: string; status: string; job_id?: string; reason?: string }>
}

export async function reprocessInvoice(invoiceId: string): Promise<ReprocessResult> {
  const response = await apiClient.post(`/invoices/${invoiceId}/reprocess`, {})
  return response as ReprocessResult
}

export async function reprocessInvoicesBulk(invoiceIds: string[]): Promise<BulkReprocessResult> {
  const response = await apiClient.post('/invoices/reprocess/bulk', { invoice_ids: invoiceIds })
  return response as BulkReprocessResult
}
